/**
 * Category Meta Tags Mapping
 * 
 * Maps category and subcategory names to their SEO meta titles and descriptions.
 * This data comes from the SEO tags CSV provided by the client's SEO team.
 * 
 * Structure: { categoryName: { subcategoryName: { title, description } } }
 * For top-level categories, subcategoryName is an empty string ''.
 */

const categoryMetaTagsMap = {
  'Accessories & Practice': {
    '': {
      title: 'Golf Accessories | Used Gear & Extras | Birdie Chance',
      description: 'Discover golf accessories including tees, towels, ball markers, training aids and more. Snap up bargains from Aussie golfers on Birdie Chance with secure escrow payments and shipping protection.'
    },
    'Ball Markers': {
      title: 'Golf Ball Markers | Collectable Designs | Birdie Chance',
      description: 'Shop golf ball markers from simple coins to branded and collectable designs, perfect for gifts or personal flair. Pick your favourites and buy safely with on-platform payments and protection.'
    },
    'Cleaning Tools': {
      title: 'Golf Club Cleaning Tools | Brushes & Kits | Birdie Chance',
      description: 'Keep your grooves sharp with golf club cleaning tools including brushes, groove cleaners and full kits. Grab practical accessories for less with secure payments and shipping protection.'
    },
    'Divot Tools': {
      title: 'Golf Divot Tools | Pitch Mark Repair | Birdie Chance',
      description: 'Fix your pitch marks in style with golf divot tools in fold-out, classic and custom designs. Browse affordable options from Aussie golfers and buy securely with escrow payments.'
    },
    'Headcovers': {
      title: 'Golf Headcovers | Driver, Fairway & Putter | Birdie Chance',
      description: 'Protect your clubs and show personality with golf headcovers, from classic knit to fun novelty styles. Shop pre-loved driver, fairway and putter covers with safe, on-platform checkout and buyer protection.'
    },
    'Tees': {
      title: 'Golf Tees for Sale | Wooden & Plastic | Birdie Chance',
      description: 'Stock up on golf tees in different lengths, colours and materials without paying pro shop prices. Buy bulk or mixed lots from Aussie golfers with secure escrow payments.'
    },
    'Towels': {
      title: 'Golf Towels | Cart & Bag Towels | Birdie Chance',
      description: 'Keep clubs and hands clean with golf towels that clip to your bag or cart, from branded to plain designs. Snap up second-hand towels with safe payments and shipping insurance.'
    },
    'Training & Practice': {
      title: 'Golf Training Aids | Grip & Swing Practice Aids | Birdie Chance',
      description: 'Sharpen your game with golf training aids for putting, swing tempo, alignment and more. Build a home practice setup on a budget by buying used gear from Aussie golfers with secure payments.'
    },
    'Other Accessories': {
      title: 'Other Golf Accessories | Birdie Chance',
      description: 'Explore other golf accessories including scorecard holders, umbrellas, organisers, valuables pouches and more. Complete your kit with safe on-platform checkout and buyer protection.'
    }
  },
  'Golf Clubs': {
    '': {
      title: 'Used Golf Clubs Australia | Drivers to Putters | Birdie Chance',
      description: 'Shop Australia\'s dedicated marketplace for used golf clubs, from premium drivers and iron sets to wedges and putters. Buy direct from Aussie golfers with secure escrow payments and buyer protection.'
    },
    'Irons': {
      title: 'Used Golf Irons | Blades & Cavity-Backs | Birdie Chance',
      description: 'Browse second-hand golf irons from forgiving cavity-backs to sleek blades and forged models. Fill the gaps in your bag for less, with secure payments and protection on every Birdie Chance purchase.'
    },
    'Wedges': {
      title: 'Used Golf Wedges | Sand, Lob & Gap | Birdie Chance',
      description: 'Shop Australia\'s best range of second-hand golf wedges, including sand, lob and gap options from leading brands. Buy from Aussie golfers with secure escrow payments and shipping protection.'
    },
    'Hybrids': {
      title: 'Used Golf Hybrids | Quality Rescue Clubs | Birdie Chance',
      description: 'Find used golf hybrids and rescue clubs to replace your long irons for less. Browse top-brand hybrids from Aussie sellers and check out safely with escrow payments and buyer protection.'
    },
    'Fairway Woods': {
      title: 'Fairway Woods for Sale Australia | Quality Used | Birdie Chance',
      description: 'Score a deal on second-hand fairway woods, from classic 3-woods and 5-woods to high-launch models. Compare listings from golfers across Australia and buy securely with protected payments.'
    },
    'Drivers': {
      title: 'Used Golf Drivers | Big Name Brands | Birdie Chance',
      description: 'Bomb it off the tee with second-hand drivers from top brands in a range of lofts and shafts. Compare real listings from Australian golfers and buy safely with protected payments and shipping cover.'
    },
    'Iron Sets': {
      title: 'Used Golf Iron Sets for Sale Australia | Birdie Chance',
      description: 'Find complete used iron sets to match your handicap, from forgiving game-improvement to compact players\' irons. Snap up quality sets from Aussie sellers with secure, on-platform payments.'
    },
    'Fairway/Hybrid Sets': {
      title: 'Fairway & Hybrid Sets | Used Club Combos | Birdie Chance',
      description: 'Tidy up the top of your bag with fairway and hybrid sets, bundled by real golfers across Australia. Buy pre-matched combos for less and check out with escrow payments and shipping protection.'
    },
    'Wedge Sets': {
      title: 'Wedge Sets for Sale | Gap, Sand & Lob | Birdie Chance',
      description: 'Dial in your short game with wedge sets covering gap, sand and lob lofts in one purchase. Browse second-hand sets from leading brands and buy securely on Birdie Chance with buyer protection.'
    },
    'Putters': {
      title: 'Used Putters for Sale | Blade & Mallet | Birdie Chance',
      description: 'Find your next flatstick with used putters from classic blades to forgiving mallets and milled models. View detailed photos, compare listings and buy safely with escrow payments and support.'
    },
    'Chippers': {
      title: 'Buy Used Golf Chippers | Short-Game Helpers | Birdie Chance',
      description: 'Take the fear out of tight lies with golf chippers and bump-and-run clubs designed for consistency. Pick up pre-loved chippers from Aussie golfers with secure payments and shipping protection.'
    },
    'Wood Sets': {
      title: 'Buy Wood Sets | Driver & Fairway Bundles | Birdie Chance',
      description: 'Pick up matched wood sets combining drivers, fairway woods and hybrids for a consistent top end. Shop used bundles from across Australia and buy with protected, on-platform payments.'
    },
    'Complete Golf Sets': {
      title: 'Complete Golf Sets | Used Club Packages | Birdie Chance',
      description: 'Get course-ready fast with complete used golf sets, ideal for beginners or upgrades. Find full packages with bag and clubs, compare prices and buy safely with escrow payments and buyer support.'
    }
  },
  'Clothing & Footwear': {
    '': {
      title: 'Golf Clothing & Shoes | Apparel Deals | Birdie Chance',
      description: 'Upgrade your on-course look with second-hand golf clothing and shoes, including polos, pants, waterproofs and spiked or spikeless footwear. Find your size and save with safe, on-platform checkout.'
    },
    'Clothing': {
      title: 'Golf Clothing | Polos, Pants & Jackets | Birdie Chance',
      description: 'Refresh your golf wardrobe with pre-loved polos, pants, shorts and outerwear. Filter by size, style and brand, then buy from Aussie golfers with safe on-platform payments and buyer protection.'
    },
    'Footwear': {
      title: 'Golf Shoes & Footwear | Spiked & Spikeless | Birdie Chance',
      description: 'Find second-hand golf shoes in spiked and spikeless styles, from classic leather to athletic designs. Filter by size and brand, then buy securely on Birdie Chance with protected payments.'
    },
    'Gloves': {
      title: 'Golf Gloves | Premium & All-Weather | Birdie Chance',
      description: 'Top up your stash with golf gloves sold singly or in multipacks, including premium leather and all-weather options. Save on essentials from Aussie golfers with secure escrow payments.'
    }
  },
  'Technology': {
    '': {
      title: 'Golf Tech & Gadgets for Sale Australia | Birdie Chance',
      description: 'Grab pre-loved golf tech like rangefinders, GPS units, launch monitors and swing analyzers. Buy smart gadgets from Aussie golfers with secure escrow payments and shipping insurance.'
    },
    'GPS Devices': {
      title: 'Golf GPS Devices | Watches & Handhelds | Birdie Chance',
      description: 'Lock in your distances with pre-loved golf GPS devices, from handheld units to GPS watches with course maps. Buy smart tech from Aussie golfers with secure escrow payments and shipping cover.'
    },
    'Launch Monitors': {
      title: 'Buy Golf Launch Monitors Australia | Birdie Chance',
      description: 'Dial in your numbers at home or the range with used personal golf launch monitors. Save on premium data-tracking tech and buy safely on Birdie Chance with protected, on-platform payments.'
    },
    'Rangefinders': {
      title: 'Golf Rangefinders | Laser Distance Finders | Birdie Chance',
      description: 'Hit the right yardage every time with used laser rangefinders, including slope and non-slope models. Compare trusted brands and buy with secure payments and shipping insurance.'
    },
    'Other Golf Tech': {
      title: 'Other Golf Tech | Training & Gadgets | Birdie Chance',
      description: 'Explore other golf tech including swing analyzers, sensors, practice nets and smart training aids. Build your home setup for less from Aussie golfers with escrow payments and buyer protection.'
    }
  },
  'Club Parts': {
    '': {
      title: 'Golf Club Parts | Heads, Shafts & Grips | Birdie Chance',
      description: 'Build or tweak your setup with used golf club parts including heads, shafts and grips. Tinker on a budget by buying from Aussie golfers with escrow payments and clear, on-platform purchase records.'
    },
    'Club Heads': {
      title: 'Golf Club Heads for Sale | Drivers to Wedges | Birdie Chance',
      description: 'Golf club heads for drivers, fairways, hybrids and wedges to build or tweak your setup. Buy quality club heads from real Aussie golfers with secure escrow payments and transaction records.'
    },
    'Grips': {
      title: 'Buy Golf Grips | Cord, Rubber & Putter | Birdie Chance',
      description: 'Refresh your feel with spare or lightly used golf grips, from cord and multi-compound to oversized putter grips. Top up your workshop stash with safe on-platform checkout and buyer protection.'
    },
    'Shafts': {
      title: 'Golf Shafts for Sale | Driver, Iron & Putter | Birdie Chance',
      description: 'Experiment with different flexes and profiles using pre-loved graphite and steel shafts for drivers, irons and putters. Tinker without full retail prices using secure escrow payments.'
    }
  },
  'Buggies': {
    '': {
      title: 'Golf Buggies | Push & Electric | Birdie Chance',
      description: 'Shop used golf buggies from simple push carts to premium electric models, plus handy accessories. Compare deals from across Australia and buy with secure payments and shipping protection.'
    },
    'Push & Pull Buggies': {
      title: 'Push & Pull Golf Buggies Australia | 3 & 4 Wheel | Birdie Chance',
      description: 'Browse used push golf buggies that make walking the course easier, from compact 3-wheelers to stable 4-wheel designs. Buy from local Aussie golfers with secure escrow payments and shipping protection.'
    },
    'Electric Buggies': {
      title: 'Electric Golf Buggies | Used Motorised Carts | Birdie Chance',
      description: 'Find used electric golf buggies and motorised carts, including remote and follow-me models. Compare deals across Australia and buy confidently with protected payments and insured shipping.'
    },
    'Buggy Accessories': {
      title: 'Golf Buggy Accessories | Seats & Holders | Birdie Chance',
      description: 'Upgrade your ride with golf buggy accessories such as seats, umbrella holders, drink holders and spare wheels. Pick up extras for less with secure on-platform payments and buyer protection.'
    }
  },
  'Bags': {
    '': {
      title: 'Golf Bags | Cart, Stand & Travel | Birdie Chance',
      description: 'Find second-hand golf bags including cart, stand, tour and travel styles from leading brands. Protect your gear and your wallet by shopping Aussie listings with secure escrow payments.'
    },
    'Cart Bags': {
      title: 'Used Golf Cart Bags for Sale in Australia | Birdie Chance',
      description: 'Shop second-hand golf cart bags with 14-way tops, plenty of pockets and trolley-friendly bases. Find big-name brands at sharp prices and buy from Aussie golfers with protected payments.'
    },
    'Stand Bags': {
      title: 'Golf Stand Bags for Sale | Lightweight Carry | Birdie Chance',
      description: 'Walk the course in comfort with second-hand golf stand bags and carry bags in lightweight designs. Browse double-strap and waterproof options, then check out securely with buyer protection.'
    },
    'Tour/Staff Bags': {
      title: 'Tour Staff Bags Australia | Premium Used | Birdie Chance',
      description: 'Own a tour-style staff golf bag at a fraction of new cost, perfect for display or serious gear haulers. Hunt for iconic tour logos and premium builds, backed by secure escrow payments.'
    },
    'Travel Bags': {
      title: 'Golf Travel Bags | Hard & Soft Cases | Birdie Chance',
      description: 'Protect your clubs in transit with used golf travel bags, from padded soft covers to hard-shell cases. Save on travel gear and enjoy XCover shipping protection and secure payments on every order.'
    },
    'Other Golf Bags': {
      title: 'Other Golf Bags | Pre-Owned Pencil & Sunday | Birdie Chance',
      description: 'Discover other golf bag styles including pencil, Sunday and practice bags for quick rounds or range sessions. Browse unique finds from Aussie golfers with safe, on-platform checkout.'
    }
  },
  'Collectables': {
    '': {
      title: 'Golf Collectables Australia | Rare Finds | Birdie Chance',
      description: 'Explore golf collectables including artwork, books, memorabilia, home decor and vintage gear. Discover unique pieces for your home, office or clubroom with secure checkout and shipping protection.'
    },
    'Artwork': {
      title: 'Golf Artwork | Prints & Collectable Art | Birdie Chance',
      description: 'Decorate your home or clubhouse with golf artwork from vintage prints to modern course scenes and signed pieces. Discover unique wall art from Aussie sellers with secure payments & shipping protection.'
    },
    'Books & Media': {
      title: 'Golf Books | Instruction, History & Stories | Birdie Chance',
      description: 'Browse golf books covering instruction, biographies, history and classic stories, perfect for the clubhouse or coffee table. Build your library for less with protected on-platform payments.'
    },
    'Memorabilia': {
      title: 'Golf Memorabilia | Signed & Rare Items | Birdie Chance',
      description: 'Hunt down golf memorabilia including signed flags, balls, photos and tournament pieces. Ideal for fans and collectors, all bought safely via escrow payments and shipping protection.'
    },
    'Home & Decor': {
      title: 'Golf Home Décor | Bar & Man Cave Pieces | Birdie Chance',
      description: 'Give your bar, office or man cave a golf twist with decor, barware and display pieces. Discover fun and stylish home items from Aussie golfers with secure, on-platform checkout.'
    },
    'Vintage & Antiques': {
      title: 'Vintage & Antique Golf Gear | Collectable Finds | Birdie Chance',
      description: 'Step back in time with vintage and antique golf gear including hickory clubs, persimmon woods and classic putters. Unearth collectable treasures with safe payments and clear buyer protection.'
    },
    'Miscellaneous': {
      title: 'Other Golf Collectables | Unique Finds | Birdie Chance',
      description: 'Explore other golf collectables that don\'t fit the usual boxes, from novelty items to one-off rarities. Browse freely and buy securely with escrow payments and full transaction records.'
    }
  },
  'Golf Balls': {
    '': {
      title: 'Used Golf Balls | Recycled Dozens | Birdie Chance',
      description: 'Stock up on used and recycled golf balls from premium brands at a fraction of new prices. Buy dozens or bulk lots from Aussie golfers and save, with secure escrow payments on every order.'
    }
  }
};

/**
 * Get meta tags for a category and optional subcategory
 * 
 * @param {string} categoryName - The category name (e.g., "Golf Clubs")
 * @param {string} [subcategoryName] - Optional subcategory name (e.g., "Irons")
 * @returns {Object|null} - Object with title and description, or null if not found
 */
export const getCategoryMetaTags = (categoryName, subcategoryName = '') => {
  if (!categoryName) {
    return null;
  }

  const category = categoryMetaTagsMap[categoryName];
  if (!category) {
    return null;
  }

  // Try to find subcategory first, then fall back to top-level category
  const subcategory = category[subcategoryName] || category[''];
  
  return subcategory || null;
};

/**
 * Normalize category/subcategory names for lookup
 * Handles case-insensitive matching and trims whitespace
 * 
 * @param {string} name - Category or subcategory name
 * @returns {string} - Normalized name
 */
const normalizeName = (name) => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  return name.trim();
};

/**
 * Get meta tags with normalized name matching
 * This provides more flexible lookup that handles minor variations
 * 
 * @param {string} categoryName - The category name
 * @param {string} [subcategoryName] - Optional subcategory name
 * @param {string} [categorySlug] - Optional category slug from URL (for better matching)
 * @param {string} [subcategorySlug] - Optional subcategory slug from URL (for better matching)
 * @returns {Object|null} - Object with title and description, or null if not found
 */
export const getCategoryMetaTagsNormalized = (
  categoryName, 
  subcategoryName = '', 
  categorySlug = null, 
  subcategorySlug = null
) => {
  const { createSlug } = require('./urlHelpers');
  
  const normalizedCategory = normalizeName(categoryName);
  const normalizedSubcategory = normalizeName(subcategoryName);

  // First try exact match
  let result = getCategoryMetaTags(normalizedCategory, normalizedSubcategory);
  if (result) {
    return result;
  }

  // Try case-insensitive match
  const categoryKeys = Object.keys(categoryMetaTagsMap);
  const matchedCategoryKey = categoryKeys.find(
    key => key.toLowerCase() === normalizedCategory.toLowerCase()
  );

  if (matchedCategoryKey) {
    const category = categoryMetaTagsMap[matchedCategoryKey];
    const subcategoryKeys = Object.keys(category);
    
    // Try to find subcategory with case-insensitive match
    const matchedSubcategoryKey = subcategoryKeys.find(
      key => key.toLowerCase() === normalizedSubcategory.toLowerCase()
    );

    if (matchedSubcategoryKey) {
      return category[matchedSubcategoryKey];
    }

    // If we have a subcategory slug, try matching by slug
    // This handles cases like "Collectable Books" (CSV) vs "Books & Media" (config)
    if (subcategorySlug) {
      const matchedBySlug = subcategoryKeys.find(key => {
        if (!key || key === '') return false;
        
        // Create slug from CSV entry name (e.g., "Collectable Books" -> "collectable-books")
        const csvSlug = createSlug(key);
        
        // Remove common prefixes like "Collectable", "Golf", etc. and try again
        const keyWithoutPrefix = key
          .replace(/^collectable\s+/i, '')
          .replace(/^golf\s+/i, '')
          .replace(/^used\s+/i, '')
          .trim();
        const csvSlugWithoutPrefix = createSlug(keyWithoutPrefix);
        
        // Extract main words from both slugs for comparison
        const csvWords = csvSlug.split('-').filter(w => w.length > 2); // Filter out short words
        const urlWords = subcategorySlug.split('-').filter(w => w.length > 2);
        
        // Check if any significant words match
        const hasMatchingWords = csvWords.some(word => urlWords.includes(word)) ||
                                 urlWords.some(word => csvWords.includes(word));
        
        // Match if:
        // 1. Exact slug match
        // 2. Slug without prefix matches
        // 3. One slug contains the other
        // 4. They share significant words (handles "collectable-books" vs "books-media")
        return csvSlug === subcategorySlug || 
               csvSlugWithoutPrefix === subcategorySlug ||
               subcategorySlug.includes(csvSlug) ||
               csvSlug.includes(subcategorySlug) ||
               (hasMatchingWords && csvWords.length > 0 && urlWords.length > 0);
      });

      if (matchedBySlug) {
        return category[matchedBySlug];
      }
    }

    // Fall back to top-level category
    if (category['']) {
      return category[''];
    }
  }

  return null;
};

